// ═══════════════════════════════════════════════════════════
//  STOCKO — Mode hors-ligne avec idempotence
//  Chaque mouvement porte un client_uid unique : même si la
//  synchro est interrompue et rejouée, aucun doublon possible.
// ═══════════════════════════════════════════════════════════

const DB_NAME = 'stocko_offline'
const DB_VERSION = 2

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = e => {
      const db = e.target.result
      if (!db.objectStoreNames.contains('produits_cache')) {
        db.createObjectStore('produits_cache', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('mouvements_pending')) {
        db.createObjectStore('mouvements_pending', { keyPath: 'client_uid' })
      }
    }
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = () => reject(req.error)
  })
}

export async function cacheProduits(produits) {
  try {
    const db = await openDB()
    const tx = db.transaction('produits_cache', 'readwrite')
    const store = tx.objectStore('produits_cache')
    store.clear()
    produits.forEach(p => store.put(p))
    return new Promise(r => { tx.oncomplete = r })
  } catch (e) { console.warn('[offline] cache produits:', e) }
}

export async function getProduitsCaches() {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction('produits_cache', 'readonly')
      const req = tx.objectStore('produits_cache').getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => resolve([])
    })
  } catch { return [] }
}

// Enregistre un mouvement en attente AVEC sa clé d'idempotence
export async function enregistrerMouvementPending(mouvement) {
  try {
    const db = await openDB()
    const clientUid = mouvement.client_uid ||
      ('offline_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8))
    return new Promise((resolve, reject) => {
      const tx = db.transaction('mouvements_pending', 'readwrite')
      const req = tx.objectStore('mouvements_pending').put({
        ...mouvement,
        client_uid: clientUid,
        created_at_local: new Date().toISOString()
      })
      req.onsuccess = () => resolve(clientUid)
      req.onerror = () => reject(req.error)
    })
  } catch (e) { console.warn('[offline] pending:', e) }
}

export async function getMouvementsPending() {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction('mouvements_pending', 'readonly')
      const req = tx.objectStore('mouvements_pending').getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => resolve([])
    })
  } catch { return [] }
}

export async function supprimerMouvementPending(clientUid) {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction('mouvements_pending', 'readwrite')
      const req = tx.objectStore('mouvements_pending').delete(clientUid)
      req.onsuccess = resolve
      req.onerror = resolve
    })
  } catch {}
}

// Synchronisation idempotente : le serveur ignore les client_uid déjà vus
export async function syncPendingMouvements(boutiqueId, enregistrerFn) {
  const pending = await getMouvementsPending()
  if (pending.length === 0) return 0

  let synced = 0
  for (const m of pending) {
    try {
      const { error } = await enregistrerFn(boutiqueId, {
        produitId: m.produitId,
        type: m.type,
        quantite: m.quantite,
        note: m.note,
        clientUid: m.client_uid   // ← la clé qui empêche les doublons
      })
      // Succès OU doublon détecté côté serveur → on peut supprimer localement
      if (!error) {
        await supprimerMouvementPending(m.client_uid)
        synced++
      }
    } catch (e) {
      console.warn('[offline] sync échouée pour', m.client_uid, e)
    }
  }
  return synced
}

// ═══════════════════════════════════════════════════════════
//  SERVICE WORKER
// ═══════════════════════════════════════════════════════════

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      console.log('[SW] Enregistré:', reg.scope)
    } catch (err) {
      console.warn('[SW] Échec:', err)
    }
  })
}
